import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Info, Plus, Trash2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ActionFeedbackContext = createContext(null);

export const useActionConfirmation = () => {
  const context = useContext(ActionFeedbackContext);
  if (!context) throw new Error('useActionConfirmation must be used within ActionFeedbackProvider');
  return context.confirmAction;
};

const actionStyles = {
  create: {
    icon: Plus,
    accent: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    button: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
  },
  update: {
    icon: Check,
    accent: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    button: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
  },
  delete: {
    icon: Trash2,
    accent: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    button: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
  },
};

const ActionFeedbackProvider = ({ children }) => {
  const [confirmation, setConfirmation] = useState(null);
  const [toast, setToast] = useState(null);
  const resolverRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const { t } = useTranslation();

  const resolveConfirmation = useCallback((confirmed) => {
    resolverRef.current?.(confirmed);
    resolverRef.current = null;
    setConfirmation(null);
  }, []);

  const confirmAction = useCallback((message) => new Promise((resolve) => {
    resolverRef.current?.(false);
    resolverRef.current = resolve;
    setConfirmation({ message });
  }), []);

  const contextValue = useMemo(() => ({ confirmAction }), [confirmAction]);

  useEffect(() => {
    const showMutationToast = (event) => {
      const type = event.detail?.type;
      if (!actionStyles[type]) return;

      setToast({ type, message: t(`actionSuccess.${type}`) });
      if (toastTimeoutRef.current) window.clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = window.setTimeout(() => setToast(null), 3500);
    };

    window.addEventListener('app:mutation-success', showMutationToast);
    return () => {
      window.removeEventListener('app:mutation-success', showMutationToast);
      if (toastTimeoutRef.current) window.clearTimeout(toastTimeoutRef.current);
    };
  }, [t]);

  useEffect(() => {
    if (!confirmation) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') resolveConfirmation(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [confirmation, resolveConfirmation]);

  const ConfirmIcon = actionStyles.delete.icon;
  const ToastIcon = toast ? actionStyles[toast.type].icon : Info;

  return (
    <ActionFeedbackContext.Provider value={contextValue}>
      {children}
      {confirmation && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onMouseDown={(event) => event.target === event.currentTarget && resolveConfirmation(false)}>
          <section role="alertdialog" aria-modal="true" aria-labelledby="action-confirmation-title" aria-describedby="action-confirmation-message" className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-gray-800">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"><ConfirmIcon size={26} /></div>
            <h2 id="action-confirmation-title" className="text-center text-xl font-bold text-gray-900 dark:text-white">{t('actionConfirmation.deleteTitle')}</h2>
            <p id="action-confirmation-message" className="mt-2 text-center text-sm text-gray-600 dark:text-gray-300">{confirmation.message}</p>
            <div className="mt-6 flex justify-center gap-3">
              <button type="button" onClick={() => resolveConfirmation(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">{t('cancel')}</button>
              <button type="button" autoFocus onClick={() => resolveConfirmation(true)} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2">{t('actionConfirmation.deleteButton')}</button>
            </div>
          </section>
        </div>
      )}
      {toast && (
        <div role="status" aria-live="polite" className={`fixed right-5 top-5 z-[110] flex max-w-sm items-center gap-3 rounded-xl px-4 py-3 text-white shadow-xl ${actionStyles[toast.type].button}`}>
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/20"><ToastIcon size={19} /></span>
          <span className="flex-1 text-sm font-semibold">{toast.message}</span>
          <button type="button" aria-label={t('close')} onClick={() => setToast(null)} className="rounded-md p-1 transition hover:bg-white/20"><X size={16} /></button>
        </div>
      )}
    </ActionFeedbackContext.Provider>
  );
};

export default ActionFeedbackProvider;
